/**
 * Session 22 — OCPP 2.0.1 NotifyMonitoringReport outbound (CP → CSMS): the
 * paged reply to GetMonitoringReport, listing the installed monitors
 * grouped by (component, variable). v2.0.1-only.
 */
export interface MonitorReportEntry {
  readonly component: string;
  readonly variable: string;
  readonly id: number;
  readonly type: string;
  readonly value: number;
  readonly severity: number;
  readonly transaction: boolean;
}

export interface NotifyMonitoringReportDomain {
  readonly requestId: number;
  readonly seqNo: number;
  readonly generatedAt: string;
  readonly tbc: boolean;
  readonly monitors: readonly MonitorReportEntry[];
}

export function buildNotifyMonitoringReportRequest(
  req: NotifyMonitoringReportDomain,
): Record<string, unknown> {
  return {
    requestId: req.requestId,
    seqNo: req.seqNo,
    generatedAt: req.generatedAt,
    tbc: req.tbc,
    // Schema requires `monitor` non-empty when present → omit on an empty page.
    ...(req.monitors.length > 0
      ? {
          monitor: req.monitors.map((m) => ({
            component: { name: m.component },
            variable: { name: m.variable },
            variableMonitoring: [
              {
                id: m.id,
                transaction: m.transaction,
                value: m.value,
                type: m.type,
                severity: m.severity,
              },
            ],
          })),
        }
      : {}),
  };
}

/** Split monitors into paginated NotifyMonitoringReport payloads. */
export function paginateNotifyMonitoringReport(
  requestId: number,
  monitors: readonly MonitorReportEntry[],
  maxItemsPerPage: number,
  generatedAt: string = new Date().toISOString(),
): readonly NotifyMonitoringReportDomain[] {
  const perPage = Math.max(1, Math.floor(maxItemsPerPage));
  if (monitors.length === 0) {
    return [{ requestId, seqNo: 0, generatedAt, tbc: false, monitors: [] }];
  }
  const pages: NotifyMonitoringReportDomain[] = [];
  for (let i = 0; i < monitors.length; i += perPage) {
    pages.push({
      requestId,
      seqNo: pages.length,
      generatedAt,
      tbc: i + perPage < monitors.length,
      monitors: monitors.slice(i, i + perPage),
    });
  }
  return pages;
}
